import { BaseEditor, Editor, Node, Path, Element as SlateElement, Transforms } from 'slate'
import { HistoryEditor } from 'slate-history'
import { ReactEditor } from 'slate-react'

import { wrapInList } from 'core-components/library/richTextEditor/helpers/listHelpers/wrapInList'

import { needEmptyLineBeforeTypes } from '../constants/needEmptyLineBeforeTypes'
import { STYLES_SET_FOR_ALIGN } from '../constants/stylesForAlign'
import {
  createColumnsRow,
  createTable,
  isTable,
} from '../customStyleView/elements/table/helpers/commonTableHelper'
import {
  ACTION_FORMATS,
  ALIGIN_TYPES,
  BLOCK_FORMATS,
  COLUMN_FORMATS,
  CustomEditor,
  FONT_COLORS_FORMATS,
  INLINE_FORMATS,
  ISeparatorElement,
  LIST_TYPES,
  MODIFIER_FORMATS,
  TActionElement,
  TCustomElement,
  TExpandBlockElement,
  TExpandBlockTitle,
  TFigureElement,
  TIFrameElement,
  TImageElement,
  TLinkElement,
  TParagraphElement,
  TTableElement,
  TTableRowElement,
  TVideoElement,
} from '../interface/custom-types'
import { VerticalAlignFormats } from '../interface/TCell'
import { TIFrame } from '../interface/TIFrame'
import { TImageSettings } from '../interface/TImage'
import { TLink, TLinkView } from '../interface/Tlink'
import { TSeparator } from '../interface/TSeparator'
import { TVideo } from '../interface/TVideo'

import { getTypes } from './customTypeHelper'
import { getListWrapOptions } from './getListWrapOptionsHelper'
import { isInAnotherNodeHelper } from './isInAnotherNodeHelper'

export const isSelectionAction = (editor: BaseEditor & ReactEditor & HistoryEditor) => {
  return editor.operations.some((op) => 'set_selection' !== op.type)
}

export const isMarkActive = (editor: CustomEditor, format: INLINE_FORMATS) => {
  const marks = Editor.marks(editor)
  return marks ? marks[format] === true : false
}

export const toggleMark = (editor: CustomEditor, format: INLINE_FORMATS) => {
  if (isMarkActive(editor, format)) {
    Editor.removeMark(editor, format)
  } else {
    Editor.addMark(editor, format, true)
  }
}

export const setColorMark = (
  editor: CustomEditor,
  colorSetting: string,
  color?: FONT_COLORS_FORMATS
) => {
  Editor.addMark(editor, colorSetting, `var(${color})`)
}

const unwrapBlockTypes = (editor: CustomEditor) => {
  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      (n.type === BLOCK_FORMATS.code || n.type === BLOCK_FORMATS.blockQuote),
    split: true,
  })
}

// https://github.com/ianstormtaylor/slate/blob/main/site/examples/richtext.tsx#L67
export const toggleBlock = (editor: CustomEditor, format: BLOCK_FORMATS, isActive: boolean) => {
  const isNewActive = !isActive
  let newProperties: Partial<SlateElement>
  if (!LIST_TYPES.includes(format)) {
    unwrapBlockTypes(editor)
  }
  if (format === BLOCK_FORMATS.blockQuote) {
    toogleBlockQuote(editor, isActive)
    return
  }
  if (format === BLOCK_FORMATS.code) {
    toogleBlockCode(editor, isActive)
    return
  }
  if (LIST_TYPES.includes(format)) {
    wrapInList(editor, format)

    return
  }
  if (isNewActive) {
    newProperties = {
      type: format,
    }
    Transforms.setNodes<SlateElement>(editor, newProperties)
  } else {
    newProperties = {
      type: BLOCK_FORMATS.paragraph,
    }

    Transforms.setNodes<SlateElement>(editor, newProperties)
  }
}

const toogleBlockQuote = (editor: CustomEditor, isActive: boolean) => {
  const isNewActive = !isActive
  let newProperties: Partial<SlateElement>

  if (isNewActive) {
    newProperties = {
      type: BLOCK_FORMATS.paragraph,
    }
    Transforms.setNodes<SlateElement>(editor, newProperties)
    const block: TCustomElement = { type: BLOCK_FORMATS.blockQuote, children: [] }
    const wrapOptions = getListWrapOptions(editor)
    Transforms.wrapNodes(editor, block, wrapOptions)
  } else {
    Transforms.unwrapNodes(editor, {
      match: (n) =>
        !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === BLOCK_FORMATS.blockQuote,
      split: true,
    })
  }
}

export const toogleBlockCode = (editor: CustomEditor, isActive: boolean) => {
  const isNewActive = !isActive
  let newProperties: Partial<SlateElement>

  if (isNewActive) {
    newProperties = {
      type: BLOCK_FORMATS.code,
      children: [],
    }
    if (SlateElement.isElement(newProperties)) {
      // If we have selected several list elements, then we need to wrap the entire list,
      // and not just the li tags
      const wrapOptions = getListWrapOptions(editor)
      Transforms.wrapNodes<SlateElement>(editor, newProperties, wrapOptions)
    }
    const block: TCustomElement = { type: BLOCK_FORMATS.pre, children: [] }
    Transforms.wrapNodes(editor, block, {
      match: (n) => SlateElement.isElement(n) && n.type === BLOCK_FORMATS.code,
    })
  } else {
    Transforms.unwrapNodes(editor, {
      match: (n) =>
        !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === BLOCK_FORMATS.pre,
      split: true,
    })
    newProperties = {
      type: BLOCK_FORMATS.paragraph,
    }
    Transforms.setNodes<SlateElement>(editor, newProperties)
  }
}

export const toggleModifier = (
  editor: CustomEditor,
  format: MODIFIER_FORMATS,
  isActive: boolean
) => {
  const isNewActive = !isActive
  let newProperties: Partial<SlateElement> = {}

  const specialAlignmentElement = Editor.above(editor, {
    match: (n) =>
      SlateElement.isElement(n) &&
      (n.type === BLOCK_FORMATS.blockQuote || LIST_TYPES.includes(n.type as BLOCK_FORMATS)),
  })
  if (specialAlignmentElement) {
    Transforms.setNodes<SlateElement>(editor, STYLES_SET_FOR_ALIGN[format], {
      at: specialAlignmentElement[1],
    })

    return
  }

  const isAlignFormat = getTypes(ALIGIN_TYPES).includes(format)

  if (isAlignFormat) {
    newProperties = {
      align: isNewActive ? format : undefined,
    }
  }
  Transforms.setNodes<SlateElement>(editor, newProperties)
}

export const removeTypeWithPath = (editor: CustomEditor, path: Path) => {
  Transforms.unwrapNodes(editor, {
    at: path,
  })
}

export const unwrapByType = (editor: CustomEditor, type: BLOCK_FORMATS) => {
  Transforms.unwrapNodes(editor, {
    match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === type,
  })
}

export const isActiveType = (editor: CustomEditor, type: BLOCK_FORMATS) => {
  const [link] = Editor.nodes(editor, {
    match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === type,
  })
  return !!link
}

const insertEmptyLine = (
  editor: CustomEditor,
  isInAnotherNode?: boolean,
  isImageNodeInserted?: boolean
) => {
  if (!editor.selection) {
    return
  }

  const paragraph: TParagraphElement = {
    type: BLOCK_FORMATS.paragraph,
    children: [{ text: '' }],
  }

  // Being inside another node, we will insert an empty line after some elements
  // to make it possible to edit content after them
  if (isInAnotherNode) {
    const needEmptyLineBeforeNode = Editor.above(editor, {
      at: editor.selection.anchor.path,
      match: (n) =>
        SlateElement.isElement(n) && needEmptyLineBeforeTypes.includes(n.type as BLOCK_FORMATS),
    })
    if (needEmptyLineBeforeNode) {
      Transforms.insertNodes(editor, paragraph, { at: Path.next(needEmptyLineBeforeNode[1]) })
    }

    return
  }

  const nextPath = Editor.after(editor, editor.selection.anchor.path)

  if (nextPath) {
    const nextNodePath = Path.parent(nextPath.path)
    const isNextNodeExist = Node.has(editor, nextNodePath)
    if (isNextNodeExist) {
      const isNextNodeImage =
        (Node.get(editor, nextNodePath) as TCustomElement).type === BLOCK_FORMATS.image
      if (isNextNodeImage) {
        Transforms.insertNodes(editor, paragraph, { at: nextNodePath })
      } else {
        const nextNodeWithEmptyLineBefore = Editor.above(editor, {
          at: nextNodePath,
          match: (n) =>
            SlateElement.isElement(n) && needEmptyLineBeforeTypes.includes(n.type as BLOCK_FORMATS),
        })
        if (nextNodeWithEmptyLineBefore || isImageNodeInserted) {
          const nodePath = nextNodeWithEmptyLineBefore
            ? nextNodeWithEmptyLineBefore[1]
            : nextNodePath
          Transforms.insertNodes(editor, paragraph, { at: nodePath })
        }
      }
    }
  } else {
    Transforms.insertNodes(editor, paragraph, { at: [editor.children.length] })
  }
}

export const insertImageLink = (editor: CustomEditor, link: TLink) => {
  const linkElement: TLinkElement = {
    type: BLOCK_FORMATS.link,
    url: link.url,
    openInNewTab: link.openInNewTab,
    children: [],
    view: 'image',
  }

  Transforms.wrapNodes(editor, linkElement, { split: true })
}

export const updateImageLink = (editor: CustomEditor, link: TLink, path: Path) => {
  Transforms.setNodes(
    editor,
    {
      url: link.url,
      openInNewTab: link.openInNewTab,
    },
    { at: path }
  )
}

export const insertLink = (editor: CustomEditor, link: TLink, type: TLinkView) => {
  if (isActiveType(editor, BLOCK_FORMATS.link)) {
    unwrapByType(editor, BLOCK_FORMATS.link)
  }

  const linkElement: TLinkElement = {
    type: BLOCK_FORMATS.link,
    url: link.url,
    openInNewTab: link.openInNewTab,
    children: [{ text: link.text }],
    view: 'text',
  }

  if (type === 'button') {
    linkElement.fontSize = link.fontSize
    linkElement.iconPosition = link.iconPosition
    linkElement.icon = link.icon
    linkElement.backgroundColor = link.backgroundColor
    linkElement.className = 'RichTextEditor__button'
    linkElement.view = 'button'
  }

  Transforms.insertNodes(editor, linkElement)
}

export const updateImage = (editor: CustomEditor, path: Path, settings: TImageSettings) => {
  // https://github.com/ianstormtaylor/slate/issues/4020
  // we need to keep the caption in a separate field because adding
  // the caption to the children array will not cause the component to rerender
  const image: TImageElement = {
    type: BLOCK_FORMATS.image,
    url: settings.url,
    children: [{ text: '' }],
    caption: settings.caption,
    width: settings.width,
    alt: settings.alt,
  }
  Transforms.setNodes(editor, image, { at: path })
}

export const insertImage = (editor: CustomEditor, settings: TImageSettings) => {
  const isInAnotherNode = isInAnotherNodeHelper(editor)

  const image: TImageElement = {
    type: BLOCK_FORMATS.image,
    url: settings.url,
    children: [{ text: '' }],
    caption: settings.caption,
    width: settings.width,
    alt: settings.alt,
  }
  Transforms.insertNodes(editor, image)
  insertEmptyLine(editor, isInAnotherNode, true)
}

export const updateCaptionUnderImage = (editor: CustomEditor, path: Path, caption: string) => {
  Transforms.setNodes(
    editor,
    { caption: caption },
    {
      at: path,
    }
  )
}

export const insertVideo = (editor: CustomEditor, settings: TVideo) => {
  const isInAnotherNode = isInAnotherNodeHelper(editor)

  const text = { text: '' }
  const video: TVideoElement = {
    type: BLOCK_FORMATS.video,
    url: settings.url,
    height: settings.height,
    children: [text],
  }
  Transforms.insertNodes(editor, video)
  insertEmptyLine(editor, isInAnotherNode)
}

export const updateVideo = (editor: CustomEditor, path: Path, settings: TVideo) => {
  const text = { text: '' }
  const video: TVideoElement = {
    type: BLOCK_FORMATS.video,
    url: settings.url,
    height: settings.height,
    children: [text],
  }
  Transforms.setNodes(editor, video, { at: path })
}

export const insertSeparator = (editor: CustomEditor, settings: TSeparator) => {
  const isInAnotherNode = isInAnotherNodeHelper(editor)
  if (isActiveType(editor, BLOCK_FORMATS.separator)) {
    unwrapByType(editor, BLOCK_FORMATS.separator)
  }
  const {
    height,
    width,
    alignment,
    backgroundColor,
    border,
    borderBottom,
    borderTop,
    borderLeft,
    borderRight,
    lineHeight,
    margin,
    marginLeft,
    marginRight,
  } = settings

  const separator: ISeparatorElement = {
    type: BLOCK_FORMATS.separator,
    className: 'RichTextEditor__separator',
    height,
    width,
    alignment,
    backgroundColor,
    border,
    borderBottom,
    borderTop,
    borderLeft,
    borderRight,
    lineHeight,
    margin,
    marginLeft,
    marginRight,
    children: [{ text: '' }],
  }

  Transforms.insertNodes(editor, separator)
  insertEmptyLine(editor, isInAnotherNode)
}

export const insertIFrame = (editor: CustomEditor, settings: TIFrame) => {
  const isInAnotherNode = isInAnotherNodeHelper(editor)

  const iframe: TIFrameElement = {
    type: BLOCK_FORMATS.iframe,
    height: settings.height,
    url: settings.url,
    name: settings.name,
    children: [{ text: '' }],
  }

  const figure: TFigureElement = {
    type: BLOCK_FORMATS.figure,
    height: settings.height,
    children: [],
  }

  Transforms.insertNodes(editor, iframe)
  Transforms.wrapNodes(editor, figure, { split: true })
  insertEmptyLine(editor, isInAnotherNode)
}

export const updateIFrame = (editor: CustomEditor, path: Path, settings: TIFrame) => {
  const text = { text: '' }
  const iframe: TIFrameElement = {
    type: BLOCK_FORMATS.iframe,
    url: settings.url,
    children: [text],
    height: settings.height,
  }
  Transforms.setNodes(editor, iframe, { at: path })
}

export const insertActionBlock = (editor: CustomEditor, format: ACTION_FORMATS) => {
  const isInAnotherNode = isInAnotherNodeHelper(editor)

  const paragraph: TParagraphElement = {
    type: BLOCK_FORMATS.paragraph,
    children: [{ text: '' }],
  }
  const actionElement: TActionElement = {
    type: BLOCK_FORMATS.action,
    format,
    children: [paragraph],
  }
  Transforms.insertNodes(editor, actionElement)
  insertEmptyLine(editor, isInAnotherNode)
}

export const updateCellVerticalAlign = (
  editor: CustomEditor,
  path: Path,
  align: VerticalAlignFormats
) => {
  Transforms.setNodes(editor, { verticalAlign: align }, { at: path })
}

export const updateCellBackgroundColor = (
  editor: CustomEditor,
  paths: Path[],
  backgroundColor: FONT_COLORS_FORMATS
) => {
  paths.forEach((path) => {
    Transforms.setNodes(editor, { backgroundColor: `var(${backgroundColor})` }, { at: path })
  })
}

export const insertColumns = (editor: CustomEditor, variant: COLUMN_FORMATS) => {
  const isInAnotherNode = isInAnotherNodeHelper(editor)

  const rowNodes: TTableRowElement = createColumnsRow(variant)
  const tableNode: TTableElement = {
    type: BLOCK_FORMATS.table,
    children: [rowNodes],
    isLayout: true,
  }
  Transforms.insertNodes(editor, tableNode)
  insertEmptyLine(editor, isInAnotherNode)
}

export const insertTable = (editor: CustomEditor, columns: number, rows: number) => {
  const isInAnotherNode = isInAnotherNodeHelper(editor)

  if (isTable(editor)) return
  const table = createTable(columns, rows)
  Transforms.insertNodes(editor, table)
  insertEmptyLine(editor, isInAnotherNode)
}

export const insertExpandBlock = (editor: CustomEditor) => {
  const title: TExpandBlockTitle = {
    type: BLOCK_FORMATS.expandBlockTitle,
    children: [{ text: '' }],
  }

  const paragraph: TParagraphElement = {
    type: BLOCK_FORMATS.paragraph,
    children: [{ text: '' }],
  }

  const expandBlock: TExpandBlockElement = {
    type: BLOCK_FORMATS.expandBlock,
    isOpen: true,
    children: [title, paragraph],
  }
  Transforms.insertNodes(editor, expandBlock)
  insertEmptyLine(editor)
}

export const updateExpandBlock = (editor: CustomEditor, path: Path, isOpen: boolean) => {
  Transforms.setNodes(editor, { isOpen: isOpen }, { at: path })
}
